import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, fetchMyPayments } from "@/lib/api";
import { PageHero, SectionEyebrow } from "@/components/Primitives";
import { ArrowUpRight, Award, BookOpen, CreditCard, Users, X, CheckCircle2, Circle, ChevronRight } from "lucide-react";
import SEO from "@/lib/seo";
import { toast } from "sonner";

const TIER_LABELS = {
  free: "Free", student: "Student", professional: "Professional",
  corporate: "Corporate", institutional: "Institutional", fellow: "Fellow",
};

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [payments, setPayments] = useState([]);
  const [enrolments, setEnrolments] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  const loadEnrolments = () => api.get("/me/enrolments").then((r) => setEnrolments(r.data)).catch(() => {});

  useEffect(() => { fetchMyPayments().then(setPayments).catch(() => {}); loadEnrolments(); }, []);

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/me/billing/portal", { return_url: window.location.origin + "/dashboard" });
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open billing portal");
      setPortalLoading(false);
    }
  };

  const cancelMembership = async () => {
    if (!window.confirm("Cancel your membership? You will keep access until the end of your paid year.")) return;
    setCancelling(true);
    try {
      await api.post("/me/membership/cancel");
      toast.success("Membership cancelled. Access continues until renewal date.");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel");
    } finally {
      setCancelling(false);
    }
  };

  const toggleModule = async (enr, moduleIndex) => {
    const isCompleted = (enr.modules_completed || []).includes(moduleIndex);
    try {
      const { data } = await api.post(`/me/enrolments/${enr.id}/progress`, { module_index: moduleIndex, completed: !isCompleted });
      setEnrolments((prev) => prev.map((e) => (e.id === enr.id ? data : e)));
    } catch {
      toast.error("Could not update progress");
    }
  };

  const tier = user?.tier || "free";
  const cancelled = !!user?.tier_cancelled_at;
  const expires = user?.tier_expires_at;

  return (
    <div data-testid="dashboard-page">
      <SEO title="Dashboard · The Cleanability Institute" description="Your member dashboard." path="/dashboard" />
      <PageHero
        testId="dashboard-hero"
        eyebrow={`Welcome, ${user?.name?.split(" ")[0] || "Member"}`}
        title="Your Institute dashboard."
        lead="Membership status, enrolments, progress, and account activity in one place."
      />

      <section className="container-x pb-12 -mt-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Award} label="Tier" value={TIER_LABELS[tier]} testId="dashboard-tier" />
          <StatCard icon={BookOpen} label="Enrolments" value={enrolments.length} testId="dashboard-enrolments-count" />
          <StatCard icon={Users} label="Role" value={user?.role === "admin" ? "Admin" : "Member"} testId="dashboard-community" />
          <StatCard icon={CreditCard} label="Payments" value={payments.length} testId="dashboard-payments" />
        </div>
      </section>

      {/* Membership */}
      <section className="container-x pb-12">
        <SectionEyebrow>Membership</SectionEyebrow>
        <div className="mt-6 rounded-2xl border border-border bg-card p-8 md:p-10" data-testid="dashboard-membership-card">
          {tier !== "free" && tier !== "fellow" ? (
            <>
              <div className="flex flex-wrap items-baseline gap-4 justify-between">
                <div>
                  <div className="font-display text-2xl md:text-3xl font-medium tracking-tight">{TIER_LABELS[tier]} membership {cancelled ? "(cancelling)" : "active"}.</div>
                  {expires && <div className="mt-2 text-sm text-muted-foreground">{cancelled ? "Access continues until" : "Renews on"} <span className="text-foreground font-medium">{expires.slice(0, 10)}</span>.</div>}
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <Link to="/membership" data-testid="dashboard-renew" className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md border border-border text-sm font-medium hover:border-[hsl(var(--accent))]">
                    {cancelled ? "Resubscribe" : "Renew early"} <ArrowUpRight className="h-3.5 w-3.5" />
                  </Link>
                  <button onClick={openPortal} disabled={portalLoading} data-testid="dashboard-billing-portal" className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md border border-border text-sm font-medium hover:border-[hsl(var(--accent))] disabled:opacity-60">
                    {portalLoading ? "Opening…" : "Manage subscription"} <ArrowUpRight className="h-3.5 w-3.5" />
                  </button>
                  {!cancelled && (
                    <button onClick={cancelMembership} disabled={cancelling} data-testid="dashboard-cancel-membership" className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md text-sm font-medium text-destructive border border-border hover:bg-secondary disabled:opacity-60">
                      <X className="h-3.5 w-3.5" /> Cancel
                    </button>
                  )}
                </div>
              </div>
            </>
          ) : tier === "fellow" ? (
            <>
              <div className="font-display text-2xl md:text-3xl font-medium tracking-tight">Fellow standing.</div>
              <p className="mt-3 text-muted-foreground">Honorary tier. Standing by invitation; no renewal required.</p>
            </>
          ) : (
            <>
              <div className="font-display text-2xl md:text-3xl font-medium tracking-tight">You are on the free tier.</div>
              <p className="mt-3 text-muted-foreground">Upgrade to unlock the full publications archive, benchmarking tools, and member rates on the Academy.</p>
              <Link to="/membership" data-testid="dashboard-upgrade" className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-md bg-foreground text-background text-sm font-medium hover:opacity-90">
                Explore tiers <ArrowUpRight className="h-4 w-4" />
              </Link>
            </>
          )}
        </div>
      </section>

      {/* Enrolments */}
      <section className="container-x pb-12">
        <SectionEyebrow>Programmes</SectionEyebrow>
        <div className="mt-6 space-y-3" data-testid="dashboard-enrolments">
          {enrolments.length === 0 ? (
            <div className="rounded-2xl border border-border bg-card p-8 text-sm text-muted-foreground">
              No enrolments yet. <Link to="/training" className="underline-link text-foreground font-medium">Browse the Academy</Link>.
            </div>
          ) : (
            enrolments.map((enr, i) => {
              const isOpen = expanded === enr.id;
              const completed = new Set(enr.modules_completed || []);
              return (
                <div key={enr.id} data-testid={`enrolment-${i}`} className="rounded-2xl border border-border bg-card overflow-hidden">
                  <button onClick={() => setExpanded(isOpen ? null : enr.id)} data-testid={`enrolment-toggle-${i}`} className="w-full p-6 md:p-7 text-left flex items-center justify-between gap-4 hover:bg-secondary/40 transition-colors">
                    <div className="min-w-0 flex-1">
                      <div className="font-mono-mini text-[hsl(var(--accent))]">{enr.course_code} · {enr.status}</div>
                      <div className="mt-2 font-display text-lg md:text-xl font-medium tracking-tight truncate">{enr.course_title}</div>
                      <div className="mt-4 h-1.5 rounded-full bg-border overflow-hidden max-w-md">
                        <div className="h-full bg-[hsl(var(--accent))]" style={{ width: `${enr.progress_pct || 0}%` }} />
                      </div>
                    </div>
                    <div className="flex items-center gap-4 shrink-0">
                      <div className="text-right">
                        <div className="font-display text-2xl font-medium">{enr.progress_pct || 0}%</div>
                        <div className="text-[10px] font-mono-mini text-muted-foreground">progress</div>
                      </div>
                      <Link to={`/dashboard/courses/${enr.id}`} onClick={(e) => e.stopPropagation()} data-testid={`enrolment-open-${i}`} className="hidden md:inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-foreground text-background text-xs font-medium hover:opacity-90">
                        Open <ArrowUpRight className="h-3 w-3" />
                      </Link>
                      <ChevronRight className={`h-4 w-4 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                    </div>
                  </button>
                  {isOpen && (
                    <div className="border-t border-border p-6 md:p-7 bg-secondary/20" data-testid={`enrolment-modules-${i}`}>
                      <div className="font-mono-mini text-muted-foreground mb-4">Modules</div>
                      <ul className="space-y-2">
                        {Array.from({ length: enr.modules_total }).map((_, idx) => {
                          const done = completed.has(idx);
                          return (
                            <li key={idx}>
                              <button
                                onClick={() => toggleModule(enr, idx)}
                                data-testid={`enrolment-module-${i}-${idx}`}
                                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm hover:bg-card text-left transition-colors"
                              >
                                {done ? <CheckCircle2 className="h-4 w-4 text-[hsl(var(--accent))]" /> : <Circle className="h-4 w-4 text-muted-foreground" />}
                                <span className={done ? "text-foreground" : "text-foreground/80"}>Module {idx + 1}</span>
                                <span className="ml-auto text-[10px] font-mono-mini text-muted-foreground">{done ? "completed" : "not started"}</span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </section>

      {/* Payments */}
      <section className="container-x pb-24">
        <SectionEyebrow>Activity</SectionEyebrow>
        <div className="mt-6 rounded-2xl border border-border bg-card overflow-hidden">
          {payments.length === 0 ? (
            <div data-testid="dashboard-no-payments" className="p-10 text-sm text-muted-foreground">No payment activity yet.</div>
          ) : (
            <div className="divide-y divide-border">
              {payments.map((p, i) => (
                <div key={p.session_id || i} data-testid={`payment-row-${i}`} className="p-5 md:p-6 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="font-display text-base md:text-lg font-medium tracking-tight">
                      {p.kind === "course" ? `Course · ${p.course_code}` : `Membership · ${TIER_LABELS[p.tier] || p.tier}`}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">{p.created_at?.slice(0, 19).replace("T", " ")}</div>
                  </div>
                  <div className="flex items-center gap-6 text-sm">
                    <div>£{Number(p.amount).toFixed(2)}</div>
                    <div className={`font-mono-mini ${p.payment_status === "paid" ? "text-[hsl(var(--accent))]" : "text-muted-foreground"}`}>{p.payment_status}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testId }) {
  return (
    <div data-testid={testId} className="rounded-2xl border border-border bg-card p-6 hover-lift">
      <Icon className="h-4 w-4 text-[hsl(var(--accent))]" />
      <div className="mt-4 font-mono-mini text-muted-foreground">{label}</div>
      <div className="mt-2 font-display text-2xl md:text-3xl font-medium tracking-tight leading-none">{value}</div>
    </div>
  );
}
